import { ApolloError, ApolloQueryResult, OperationVariables, useQuery } from "@apollo/client";
import { useState } from "react";
import { getRCALibraryOrderBy } from "./helpers";
import { IRCA, IRCALibraryTableOperationFilters } from "./RCALibraryEntityInterfaces";
import { getRCALibrary, getRCALibraryCount } from "api/queries/RCA/RCALibraryAPI";

/** Where clause shape for RCA library/count queries (Hasura-style filter object) */
export type RCALibraryWhere = Record<string, unknown>;

export interface UseRCALibraryListReturn {
    items: IRCA[];
    totalCount: number;
    loading: boolean;
    apolloQueryError: ApolloError | undefined;
    refetch: (variables?: Partial<OperationVariables>) => Promise<ApolloQueryResult<unknown>>;
}

/**
 * Shared hook for the count-then-list RCA library query pattern.
 * Used by GetRCALibrary and GetUserRCAs to avoid duplicating the two-query flow and state.
 */
export function useRCALibraryList(
    whereVariables: RCALibraryWhere,
    tableOperationFilters: IRCALibraryTableOperationFilters,
): UseRCALibraryListReturn {
    const [items, setItems] = useState<IRCA[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);

    useQuery(getRCALibraryCount, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {
            if (data?.rca_aggregate) {
                const count: number = data.rca_aggregate.aggregate?.count ?? 0;
                setTotalCount(count);
                if (count < 1) {
                    setItems([]);
                }
            }
        },
        variables: {
            where: whereVariables,
        },
    });

    const { loading, error, refetch } = useQuery(getRCALibrary, {
        fetchPolicy: "no-cache",
        skip: !totalCount,
        onCompleted: (data) => {
            if (data?.rca && Array.isArray(data.rca)) {
                setItems(data.rca);
            }
        },
        variables: {
            limit: tableOperationFilters.limit,
            offset: tableOperationFilters.offset,
            order_by: getRCALibraryOrderBy(tableOperationFilters),
            where: whereVariables,
        },
    });

    return {
        items,
        totalCount,
        loading,
        apolloQueryError: error,
        refetch,
    };
}
